import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import PopupForm from 'components/molecules/popup/form/PopupForm';
import { FormikHelpers } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiArrowsLeftRight } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetMusicProductDigitalLightList } from 'services/music-product-digital/light';
import { usePutImportArtistsAndTracksFromPhysical } from 'services/music-product-digital';
import { IMusicProductDigitalResponse } from 'services/music-product-digital/types/query';
import { usePutImportArtistsAndTracksFromDigital } from 'services/music-product-physical';
import { useGetMusicProductPhysicalLightList } from 'services/music-product-physical/light';
import { IMusicProductPhysicalResponse } from 'services/music-product-physical/types/query';
import { ITitle } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { object, string } from 'yup';

export type ImportArtistsAndTracksDirection = 'fromPhysical' | 'fromDigital';

interface IImportArtistsAndTracksForm {
  sourceProductId: string;
}

interface IImportArtistsAndTracksPopupProps {
  direction: ImportArtistsAndTracksDirection;
  targetProductId: string;
  version: number;
  onDigitalSuccess?: (data: IMusicProductDigitalResponse) => void;
  onPhysicalSuccess?: (data: IMusicProductPhysicalResponse) => void;
}

const formatProductLabel = (mainTitle: ITitle, ean?: string) => {
  const title = mainTitle.version
    ? `${mainTitle.title} (${mainTitle.version})`
    : mainTitle.title;

  return ean ? `${title} — ${ean}` : title;
};

const importArtistsAndTracksValidator = object().shape({
  sourceProductId: string().required(i18next.t('formik-error.required')),
});

const ImportArtistsAndTracksPopup = ({
  direction,
  targetProductId,
  version,
  onDigitalSuccess,
  onPhysicalSuccess,
}: IImportArtistsAndTracksPopupProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const physicalProducts = useGetMusicProductPhysicalLightList();
  const digitalProducts = useGetMusicProductDigitalLightList();

  const importFromPhysical = usePutImportArtistsAndTracksFromPhysical();
  const importFromDigital = usePutImportArtistsAndTracksFromDigital();

  const sourceOptions =
    direction === 'fromPhysical'
      ? (physicalProducts.data?.map(product => ({
          value: product.id,
          label: formatProductLabel(product.mainTitle, product.ean),
        })) ?? [])
      : (digitalProducts.data?.map(product => ({
          value: product.id,
          label: formatProductLabel(product.mainTitle, product.ean),
        })) ?? []);

  const isLoading =
    direction === 'fromPhysical'
      ? importFromPhysical.isPending
      : importFromDigital.isPending;

  const onSubmit = async (
    values: IImportArtistsAndTracksForm,
    helpers: FormikHelpers<IImportArtistsAndTracksForm>,
    actions: PopupActions | null
  ) => {
    if (direction === 'fromPhysical') {
      await importFromPhysical.mutateAsync(
        {
          digitalMusicProductId: targetProductId,
          physicalMusicProductId: values.sourceProductId,
          version,
        },
        {
          onSuccess: data => {
            helpers.resetForm();
            actions?.close();
            queryClient.invalidateQueries({
              queryKey: ['music-product-digital', targetProductId],
            });
            onDigitalSuccess?.(data);
          },
          onError: error => onErrorShowAllErrorsToast(error),
        }
      );
      return;
    }

    await importFromDigital.mutateAsync(
      {
        physicalMusicProductId: targetProductId,
        digitalMusicProductId: values.sourceProductId,
        version,
      },
      {
        onSuccess: data => {
          helpers.resetForm();
          actions?.close();
          queryClient.invalidateQueries({
            queryKey: ['music-product-physical', targetProductId],
          });
          onPhysicalSuccess?.(data);
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  return (
    <PopupForm<IImportArtistsAndTracksForm>
      trigger={
        <Button variant={BUTTON_VARIANT.white}>
          <Button.Icon icon={<PiArrowsLeftRight />} />
          <Button.Text text={t('import-artists-and-tracks.button')} />
        </Button>
      }
      header={t('import-artists-and-tracks.header')}
      initialValues={{ sourceProductId: '' }}
      validationSchema={importArtistsAndTracksValidator}
      onSubmit={onSubmit}
      submitButton={
        <Button
          type='submit'
          variant={BUTTON_VARIANT.submit}
          isLoading={isLoading}>
          <Button.Text text={t('button.import')} />
        </Button>
      }>
      <p className='text-sm opacity-70 mb-4 text-center max-w-80'>
        {t('import-artists-and-tracks.description')}
      </p>
      <FormInput.Select
        name='sourceProductId'
        label={t(
          direction === 'fromPhysical'
            ? 'import-artists-and-tracks.source-physical-product'
            : 'import-artists-and-tracks.source-digital-product'
        )}
        options={sourceOptions}
        isSearchable
        isLoading={
          direction === 'fromPhysical'
            ? physicalProducts.isLoading
            : digitalProducts.isLoading
        }
      />
    </PopupForm>
  );
};

export default ImportArtistsAndTracksPopup;
